export type UserRole =
  | "cure"
  | "vicaire"
  | "econome"
  | "secretaire_general"
  | "secretaire"
  | "responsable_mouvement"
  | "conseiller"
  | "benevole";

// Pages 2..92 (89 = Guide du paroissien ; 90 = Mes reversements à l'économat ;
// 91 = Encaissements au guichet, C2 du 19/10/2026 ; 92 = Horaires des messes,
// 27/10/2026).
// Toute nouvelle page doit étendre cette borne, sinon elle reste invisible
// dans la barre latérale, y compris pour le curé.
const ALL_PAGES = Array.from({ length: 91 }, (_, i) => i + 2);

// Droits alignés sur le manuel 2 « L'espace de votre paroisse » (09/10/2026) :
// tableaux « Qui a accès à quoi » (p. 12-13) et « Les accès de… » de chaque
// rôle (p. 24, 33, 42, 51, 60, 69, 78, 87). Écarts assumés, listés dans
// docs/suivi-pages/paroisses.md (historique du 09/10/2026) :
//  - 81 = « Mobile Money et dons en ligne » (/mobile-money). Décision PO du
//    06/10/2026 : les dons électroniques sont visibles des paroisses pour
//    information. Le curé et l'économe gèrent la page ; vicaire, secrétaire
//    général et conseiller la CONSULTENT en lecture seule (le manuel,
//    antérieur, la réserve au curé et à l'économe). La secrétaire n'y a plus
//    accès (décision du PO du 22/10/2026, D10) : ni montant de don en ligne
//    ni nom de donateur ; donation-service lui refuse aussi la liste, la
//    synthèse et l'export.
//  - Conseiller : 3 = annuaire en lecture (kit « fiche CPC »). Pas de 13
//    (« Analytics prédictifs », prévisions de dons) : « Finances : aucun
//    accès » (p. 13 et 78) l'emporte sur la ligne « Analytics prédictifs ».
export const ROLE_PAGE_ACCESS: Record<UserRole, number[]> = {
  cure: ALL_PAGES,
  // Vicaire (31 écrans, p. 33) : « Finances : cockpit seul » et « Mesure :
  // analytics pastoral seul » : pas de 13.
  // 92 (Horaires des messes, 27/10/2026) : le vicaire et la secrétaire
  // saisissent les horaires de leur paroisse, comme le curé (parish-service
  // ouvre déjà /masses à ces trois rôles).
  vicaire: [2, 3, 4, 5, 6, 7, 8, 12, 18, 30, 31, 41, 42, 43, 44, 45, 46, 50, 51, 52, 53, 61, 62, 78, 79, 80, 81, 82, 83, 86, 87, 92],
  // Économe (p. 42) : pas de 58 (« Analytics et qualité : curé, conseiller »).
  // 91 (C2, 19/10/2026) : l'économe garde la saisie du cockpit et voit aussi
  // les encaissements du guichet de toute la paroisse.
  econome: [2, 8, 9, 21, 22, 23, 27, 28, 29, 34, 37, 39, 40, 63, 64, 68, 69, 70, 71, 73, 74, 75, 77, 81, 84, 86, 88, 90, 91],
  // Secrétaire général (p. 51) : « Communication : complet » inclut
  // l'assistant IA (12), que community-service lui ouvre déjà.
  secretaire_general: [2, 3, 10, 11, 12, 14, 15, 24, 36, 37, 38, 47, 48, 49, 62, 65, 66, 67, 76, 79, 80, 81, 86, 87, 88, 89],
  // 45 (Portail des mouvements) : la secrétaire voit et tranche les demandes
  // d'adhésion (demande PO du 17/10/2026 ; community-service l'y autorise déjà).
  // 91 (C2, décision PO du 19/10/2026) : elle encaisse au guichet et ne voit
  // que ses propres encaissements ; toujours ni cockpit (8), ni clôture (9),
  // ni « Mes reversements » (90).
  // Pas de 81 « Dons en ligne » (D10, décision du PO du 22/10/2026) : son
  // accueil garde le fil « En direct » sans montant ni nom.
  // 42 (Préparation des sacrements, décision PO du 31/10/2026) : elle traite les
  // demandes de documents faites dans CATHO+ (certificats de baptême, de mariage,
  // attestations), comme le curé ; sacrement-service l'y autorise.
  secretaire: [2, 3, 4, 6, 7, 24, 25, 26, 30, 31, 36, 38, 42, 45, 66, 67, 77, 80, 83, 86, 87, 91, 92],
  responsable_mouvement: [2, 20, 24, 25, 41, 43, 44, 45, 46, 65, 82, 85, 86, 87],
  conseiller: [2, 3, 14, 52, 54, 55, 58, 59, 60, 62, 72, 81, 84, 86, 88],
  // Bénévole certifiée (p. 87) : 84 « Bourses et aides sociales » en lecture,
  // sans les noms (solidarite-service ne lui sert que la vue restreinte).
  benevole: [2, 3, 24, 25, 45, 84, 86, 87],
};

/**
 * Métadonnées des rôles consommables HORS contexte React (color = constante UI,
 * pas de string traduisible). Les libellés et descriptions sont gérés par
 * i18n : voir `auth.roles.<role>.{label,description}` (FR + EN).
 *
 * Les helpers `getRoleLabel` / `getRoleDescription` plus bas renvoient
 * uniquement un fallback brut (la clé du rôle) — ils existent pour la
 * back-compat des tests unitaires ; les composants UI doivent utiliser
 * `t(\`auth.roles.${role}.label\`)` directement.
 */
export const ROLE_INFO: Record<UserRole, { color: string }> = {
  cure:                  { color: "#C9A961" },
  vicaire:               { color: "#2196F3" },
  econome:               { color: "#27AE60" },
  secretaire_general:    { color: "#E67E22" },
  secretaire:            { color: "#8e44ad" },
  responsable_mouvement: { color: "#e74c3c" },
  conseiller:            { color: "#1A2B4A" },
  benevole:              { color: "#7f8c8d" },
};

export function canAccessPage(role: string, pageId: number): boolean {
  const access = ROLE_PAGE_ACCESS[role as UserRole];
  if (!access) return false;
  return access.includes(pageId);
}

/**
 * Tableau de bord financier de finance-service
 * (`GET /api/finance/dashboards/parish/:id`) : le serveur ne l'ouvre qu'au
 * curé et à l'économe. Le vicaire a le cockpit (page 8) mais « pas de
 * finances » au sens comptable (manuel 2, p. 33) : aucun appel pour lui.
 */
export const ROLES_TABLEAU_FINANCIER: readonly UserRole[] = ["cure", "econome"];

export function voitTableauFinancier(role: string): boolean {
  return ROLES_TABLEAU_FINANCIER.includes(role as UserRole);
}

/**
 * Accès « Finances » du manuel 2 (p. 13 et « Les accès de… » de chaque rôle) :
 * le curé et l'économe ont les finances, le vicaire le cockpit seul, en
 * lecture. Le secrétaire général, la secrétaire (« Collecte et dons : saisie
 * seule »), la responsable de mouvement, le conseiller et la bénévole :
 * « Finances : aucun accès ». La règle suit l'écran 8 (Cockpit financier) de
 * la matrice : ces profils ne voient sur l'accueil aucun montant de don, de
 * clôture ou de comptabilité, et ces chiffres ne sont pas demandés pour eux.
 * L'écran 81 (Mobile Money), ouvert en consultation à plusieurs de ces rôles
 * par la décision du 06/10/2026, n'est pas concerné.
 */
export function aAccesFinances(role: string): boolean {
  return canAccessPage(role, 8);
}

/**
 * Dons de la paroisse (`GET /api/donations/admin/parish-list`) : mêmes rôles
 * que l'écran 81 « Mobile Money et dons en ligne ». La responsable de
 * mouvement et la bénévole n'y ont pas accès : leur fil d'activité ne montre
 * que les intentions de messe.
 */
export function voitDons(role: string): boolean {
  return canAccessPage(role, 81);
}

/**
 * Lectures que le serveur réserve à certains rôles paroisse (miroir des
 * @RolesAllowed des services). Un écran partagé par plusieurs rôles
 * n'appelle une source que si le rôle la lit : aucun refus (403) inutile.
 *  - visites pastorales (parish-service) : confidentielles ;
 *  - collectes (donation-service) : finances ;
 *  - segmentation (parish-service, statistiques d'engagement) ;
 *  - statistiques financières (finance-service) ;
 *  - réservations d'espaces (parish-service).
 */
export type LectureServeur = "visitesPastorales" | "collectes" | "segmentation" | "statsFinance" | "reservations";

const PASTORAL_ET_ADMIN: readonly UserRole[] = ["cure", "vicaire", "econome", "secretaire", "secretaire_general"];

const LECTURES_SERVEUR: Record<LectureServeur, readonly UserRole[]> = {
  visitesPastorales: PASTORAL_ET_ADMIN,
  collectes: PASTORAL_ET_ADMIN,
  segmentation: PASTORAL_ET_ADMIN,
  // finance-service, GET /api/finance/stats (relayé par le BFF de l'accueil).
  statsFinance: PASTORAL_ET_ADMIN,
  // parish-service, GET /api/reservations : la responsable de mouvement y lit
  // sa paroisse (Action 34) ; ni la bénévole, ni l'économe, ni le conseiller.
  reservations: ["cure", "vicaire", "secretaire", "responsable_mouvement"],
};

export function peutLire(role: string, source: LectureServeur): boolean {
  return LECTURES_SERVEUR[source].includes(role as UserRole);
}

export function getAccessiblePages(role: string): number[] {
  return ROLE_PAGE_ACCESS[role as UserRole] || [];
}

/**
 * Fallback HORS contexte React. Renvoie la clé brute du rôle — pour un
 * libellé localisé, préférer `t(\`auth.roles.${role}.label\`)` côté composant.
 */
export function getRoleLabel(role: string): string {
  return role;
}

/**
 * Fallback HORS contexte React. Renvoie la clé brute du rôle si connu, "" sinon.
 * Pour une description localisée, préférer `t(\`auth.roles.${role}.description\`)`.
 */
export function getRoleDescription(role: string): string {
  return ROLE_INFO[role as UserRole] ? role : "";
}

export function getRoleColor(role: string): string {
  return ROLE_INFO[role as UserRole]?.color || "#7f8c8d";
}
